import { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { getFirestore, doc, onSnapshot, setDoc } from 'firebase/firestore';

interface MaintenanceContextType {
  maintenance: boolean;
  setMaintenance: (value: boolean) => void;
}

const MaintenanceContext = createContext<MaintenanceContextType | undefined>(undefined);

export const useMaintenance = () => {
  const context = useContext(MaintenanceContext);
  if (!context) {
    throw new Error('useMaintenance must be used within a MaintenanceProvider');
  }
  return context;
};

export const MaintenanceProvider = ({ children }: { children: ReactNode }) => {
  const [maintenance, setMaintenanceState] = useState<boolean>(false);
  const db = getFirestore();

  useEffect(() => {
    const maintenanceDoc = doc(db, 'site', 'maintenance');
    const unsubscribe = onSnapshot(maintenanceDoc, (snapshot) => {
      const data = snapshot.data();
      setMaintenanceState(!!data?.active);
    });
    return unsubscribe;
  }, [db]);

  const setMaintenance = async (value: boolean) => {
    const maintenanceDoc = doc(db, 'site', 'maintenance');
    await setDoc(maintenanceDoc, { active: value }, { merge: true });
  };

  return (
    <MaintenanceContext.Provider value={{ maintenance, setMaintenance }}>
      {children}
    </MaintenanceContext.Provider>
  );
}; 